import { LoneWorkingGroupSettings, LoneWorkingSiteSettings } from '@cloudsign/shared';
import { t } from '../../../lib/i18n';
import { Button, Card, CheckboxField, Spinner, TextField } from '../../../components/ui';
import { ErrorLine, ErrorPanel } from '../../../components/ErrorPanel';
import { useToastOnSuccess } from '../../../components/Toast';
import type { SettingsCardProps } from '../../../components/SettingsCards';
import { useSettingsDraft } from '../../../lib/useSettingsDraft';
import { ContactRows, contactProblem } from './ContactRows';
import type { ContactDraft } from './ContactRows';

type GroupSettings = LoneWorkingGroupSettings;

function NumberField({ label, hint, value, min, max, disabled, onChange }: { label: string; hint?: string; value: number; min: number; max: number; disabled: boolean; onChange: (n: number) => void }) {
  const bad = !Number.isInteger(value) || value < min || value > max;
  return (
    <TextField
      label={label}
      type="number"
      min={min}
      max={max}
      value={String(value)}
      disabled={disabled}
      {...(hint ? { hint } : {})}
      error={bad ? t('p3lw.settings.between', { min, max }) : undefined}
      onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
    />
  );
}

/** Group card: check-in interval, grace times, prompts off, manual start, contacts editing, location on check-in, fallback contacts. */
export function LoneWorkingGroupCard({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/groups/${groupId}/settings`, 'lone-working', LoneWorkingGroupSettings);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: GroupSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<GroupSettings>) => s.setDraft({ ...d, ...patch });
  const fallbackRows: ContactDraft[] = d.fallbackContacts.map((c) => ({ name: c.name, phone: c.phone ?? '', email: c.email ?? '' }));
  const fallbackOk = fallbackRows.every((r) => contactProblem(r) === null);
  const off = !canEdit;
  return (
    <Card
      title={t('p3lw.settings.groupTitle')}
      description={t('p3lw.settings.groupDescription')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!s.changed || !s.valid || !fallbackOk || s.save.isPending} onClick={() => s.save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <ErrorLine error={s.save.error} />
        <CheckboxField label={t('p3lw.settings.enabled')} hint={t('p3lw.settings.enabledHint')} checked={d.enabled} disabled={off} onChange={(e) => set({ enabled: e.target.checked })} />
        <CheckboxField label={t('p3lw.settings.promptsOff')} hint={t('p3lw.settings.promptsOffHint')} checked={d.promptsOff} disabled={off} onChange={(e) => set({ promptsOff: e.target.checked })} />
        <div className="grid gap-4 sm:grid-cols-3">
          <NumberField label={t('p3lw.settings.interval')} hint={t('p3lw.settings.intervalHint')} value={d.intervalMinutes} min={10} max={720} disabled={off} onChange={(n) => set({ intervalMinutes: n })} />
          <NumberField label={t('p3lw.settings.grace')} hint={t('p3lw.settings.graceHint')} value={d.graceMinutes} min={2} max={60} disabled={off} onChange={(n) => set({ graceMinutes: n })} />
          <NumberField label={t('p3lw.settings.secondGrace')} hint={t('p3lw.settings.secondGraceHint')} value={d.secondGraceMinutes} min={2} max={60} disabled={off} onChange={(n) => set({ secondGraceMinutes: n })} />
        </div>
        <CheckboxField label={t('p3lw.settings.manualStart')} checked={d.allowManualStart} disabled={off} onChange={(e) => set({ allowManualStart: e.target.checked })} />
        <CheckboxField label={t('p3lw.settings.membersEdit')} hint={t('p3lw.settings.membersEditHint')} checked={d.membersEditContacts} disabled={off} onChange={(e) => set({ membersEditContacts: e.target.checked })} />
        <CheckboxField label={t('p3lw.settings.shareLocation')} hint={t('p3lw.settings.shareLocationHint')} checked={d.shareLocationOnCheckIn} disabled={off} onChange={(e) => set({ shareLocationOnCheckIn: e.target.checked })} />
        <ContactRows
          label={t('p3lw.settings.fallback')}
          rows={fallbackRows}
          disabled={off}
          onChange={(rows) => set({ fallbackContacts: rows.map((r) => ({ name: r.name, phone: r.phone.trim() === '' ? null : r.phone, email: r.email.trim() === '' ? null : r.email })) })}
        />
        <p className="text-xs text-slate-600">{t('p3lw.settings.fallbackHint')}</p>
      </div>
    </Card>
  );
}

/** Site card: everyone signed in here is lone working. */
export function LoneWorkingSiteCard({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/sites/${siteId}/settings`, 'lone-working', LoneWorkingSiteSettings);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d = s.draft;
  if (!d) return <Spinner />;
  return (
    <Card
      title={t('p3lw.settings.siteTitle')}
      description={t('p3lw.settings.siteDescription')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!s.changed || s.save.isPending} onClick={() => s.save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <ErrorLine error={s.save.error} />
        <CheckboxField label={t('p3lw.settings.siteEnabled')} hint={t('p3lw.settings.siteEnabledHint')} checked={d.enabled} disabled={!canEdit} onChange={(e) => s.setDraft({ ...d, enabled: e.target.checked })} />
      </div>
    </Card>
  );
}

/** What the generic settings pages (sites and groups) mount for the `lone-working` module key. */
export function LoneWorkingSettingsCard({ scope, scopeId, canEdit }: SettingsCardProps) {
  return scope === 'groups' ? <LoneWorkingGroupCard groupId={scopeId} canEdit={canEdit} /> : <LoneWorkingSiteCard siteId={scopeId} canEdit={canEdit} />;
}
